import React from 'react';
import { Box, Grid, IconButton, MenuItem, Select, SelectChangeEvent, TextField, styled } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ConditionPredicate as Comparator } from '@pepperhq/sdk';
import { IConditionValue } from 'components/campaigns/models/CampaignItem';
import {
    ConditionVariant,
    TimeConditionKey,
    TimeOfDay
} from 'components/campaigns/models/CampaignTriggerCondition';
import { Option } from 'lib/types';
import { AmountCondition } from './AmountCondition';
import { LocationCondition } from './LocationCondition';
import { SegmentCondition } from './SegmentCondition';
import { TimeBasedConditions } from './TimeBasedConditions';
import { VoucherCondition } from './VoucherCondition';

const PREFIX = 'ConditionValue';

const classes = {
    root: `${PREFIX}-root`,
    deleteIcon: `${PREFIX}-deleteIcon`
};

const StyledGrid = styled(Grid)(({ theme }) => ({
    '& $deleteIcon': {
        opacity: 0
    },
    '&:hover': {
        '& $deleteIcon': {
            opacity: 1
        }
    },
    [`& .${classes.deleteIcon}`]: {
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        transition: 'opacity 0.15s',
        marginRight: theme.spacing(1)
    }
}));

interface ConditionValueProps {
    value: IConditionValue;
    index: number;
    conditionVariants: Option[][];
    onValueChange: (
        value: string | string[] | number | TimeOfDay[],
        index: number,
        comparator?: Comparator
    ) => void;
    onVariantChange: (value: string, index: number) => void;
    onDelete: (index: number) => void;
    deletable: boolean;
    onVoucherCreate: (value: string, innderIndex: number) => void;
}

export const ConditionValue: React.FC<ConditionValueProps> = props => {
    const {
        value,
        onValueChange,
        index,
        conditionVariants,
        onVariantChange,
        onDelete,
        deletable,
        onVoucherCreate
    } = props;

    const handleVariantChange = React.useCallback(
        (e: SelectChangeEvent) => {
            onVariantChange(e.target.value, index);
        },
        [index, onVariantChange]
    );
    const handleValueChange = React.useCallback(
        (value: number | TimeOfDay[] | string) => {
            onValueChange(value, index);
        },
        [index, onValueChange]
    );
    const handleValueTextChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            onValueChange(e.target.value, index);
        },
        [index, onValueChange]
    );
    const handleSearchableValueChange = React.useCallback(
        (value: string | string[]) => {
            onValueChange(value, index);
        },
        [index, onValueChange]
    );
    const handleAmountChange = React.useCallback(
        (value: string, comparator: Comparator) => {
            onValueChange(value, index, comparator);
        },
        [index, onValueChange]
    );
    const handleDelete = React.useCallback(() => {
        onDelete(index);
    }, [onDelete, index]);
    const handleVoucherCreate = React.useCallback(
        (title: string) => {
            onVoucherCreate(title, index);
        },
        [index, onVoucherCreate]
    );

    return (
        <StyledGrid item className={classes.root}>
            <Grid container spacing={2} alignItems="flex-end">
                <Grid item>
                    <Box minWidth={200}>
                        <Select
                            fullWidth
                            value={(value && value.key) || ''}
                            onChange={handleVariantChange}
                            placeholder="Choose a trigger"
                        >
                            {conditionVariants.map(item =>
                                item.map(({ value, label }) => (
                                    <MenuItem key={`select-condition-type-${value}`} value={value}>
                                        {label}
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                    </Box>
                </Grid>
                {value.key in TimeConditionKey && (
                    <Grid item>
                        <Box minWidth={200}>
                            <TimeBasedConditions
                                type={value.key as TimeConditionKey}
                                value={value.value}
                                onChange={handleValueChange}
                            />
                        </Box>
                    </Grid>
                )}
                {value.key === ConditionVariant.location && (
                    <Grid item>
                        <Box minWidth={250}>
                            <LocationCondition value={value.value} onChange={handleSearchableValueChange} />
                        </Box>
                    </Grid>
                )}
                {value.key === ConditionVariant.audience && (
                    <Grid item>
                        <Box minWidth={250}>
                            <SegmentCondition value={value.value} onChange={handleSearchableValueChange} />
                        </Box>
                    </Grid>
                )}
                {value.key === ConditionVariant.voucher && (
                    <Grid item>
                        <Box minWidth={250}>
                            <VoucherCondition
                                value={value.value}
                                onChange={handleSearchableValueChange}
                                onCreate={handleVoucherCreate}
                            />
                        </Box>
                    </Grid>
                )}
                {value.key === ConditionVariant.product && (
                    <Grid item>
                        <Box minWidth={250}>
                            <TextField
                                fullWidth
                                placeholder="Enter product Identifier"
                                value={value.value || ''}
                                onChange={handleValueTextChange}
                            />
                        </Box>
                    </Grid>
                )}
                {value.key === ConditionVariant.category && (
                    <Grid item>
                        <Box minWidth={250}>
                            <TextField
                                fullWidth
                                placeholder="Enter category Identifier"
                                value={value.value || ''}
                                onChange={handleValueTextChange}
                            />
                        </Box>
                    </Grid>
                )}
                {value.key === ConditionVariant.amount && (
                    <Grid item>
                        <Box minWidth={250}>
                            <AmountCondition
                                value={value.value || ''}
                                onChange={handleAmountChange}
                                comparator={value.comparator}
                            />
                        </Box>
                    </Grid>
                )}
                <div className={classes.deleteIcon}>
                    {deletable && (
                        <IconButton color="primary" onClick={handleDelete} size="large">
                            <DeleteOutlineIcon />
                        </IconButton>
                    )}
                </div>
            </Grid>
        </StyledGrid>
    );
};
